import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import PanelSkeleton from "../PanelSkeleton";
import { FaRegTrashAlt } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { FiSave, FiRotateCw, FiPlus, FiX, FiMaximize2 } from 'react-icons/fi';

import styles from "../../styles/dimensionsPanel.module.css";

const emptyDim = { width: "", height: "", mattePrice: "", glossPrice: "", vanType: "small" };

const DimensionsPanel = () => {
  const [dimension, setDimension]       = useState(emptyDim);
  const [dimensions, setDimensions]     = useState([]);
  const [addDim, setAddDim]             = useState(false);
  const [editingDimId, setEditingDimId] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [submitting, setSubmitting]     = useState(false);
  const formRef = useRef(null);

  const handleActive = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setEditingDimId(null);
    setDimension(emptyDim);
    setAddDim(true);
  };

  const handleInactive = () => {
    if (addDim && formRef.current) {
      formRef.current.classList.add("dimFormExit");
      setTimeout(() => setAddDim(false), 260);
    }
  };

  const fetchDimensions = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/dimensions`,
        { withCredentials: true }
      );
      setDimensions(res.data.data);
    } catch {
      toast.error(<span className="toast">لم نتمكن من تحميل البيانات. حاول مرة اخرى</span>);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDimensions(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDimension(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDimId) {
        confirmAlert({
          message: `تحديث المقاس ؟`,
          buttons: [
            {
              label: 'نعم',
              onClick: async () => {
                setSubmitting(true);
                try {
                  await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/api/products/updateDimension/${editingDimId}`,
                    {
                      mattePrice: Number(dimension.mattePrice),
                      glossPrice: Number(dimension.glossPrice),
                      vanType: dimension.vanType,
                    },
                    { withCredentials: true }
                  );
                  toast.success(<span className="toast">تم تحديث البيانات بنجاح</span>);
                  resetForm();
                  setAddDim(false);
                  await fetchDimensions();
                } catch {
                  toast.error(<span className="toast">لم نتمكن من تحديث البيانات. حاول مرة اخرى</span>);
                } finally {
                  setSubmitting(false);
                }
              },
            },
            { label: 'لا' },
          ],
        });
      } else {
        confirmAlert({
          message: `إضافة مقاس ؟`,
          buttons: [
            {
              label: 'نعم',
              onClick: async () => {
                setSubmitting(true);
                try {
                  await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/products/addDimension`,
                    {
                      width:      Number(dimension.width),
                      height:     Number(dimension.height),
                      mattePrice: Number(dimension.mattePrice),
                      glossPrice: Number(dimension.glossPrice),
                      vanType:    dimension.vanType,
                    },
                    { withCredentials: true }
                  );
                  toast.success(<span className="toast">تم إضافة المقاس بنجاح</span>);
                  resetForm();
                  setAddDim(false);
                  await fetchDimensions();
                } catch {
                  toast.error(<span className="toast">لم نتمكن من إضافة المقاس. حاول مرة اخرى</span>);
                } finally {
                  setSubmitting(false);
                }
              },
            },
            { label: 'لا' },
          ],
        });
      }
    } catch {
      toast.error(<span className="toast">حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى</span>);
    }
  };

  const resetForm = () => {
    setDimension(emptyDim);
    setEditingDimId(null);
  };

  const handleEdit = (dim) => {
    setAddDim(true);
    setEditingDimId(dim.id);
    setDimension({ width: dim.width, height: dim.height, mattePrice: dim.mattePrice, glossPrice: dim.glossPrice, vanType: dim.vanType || "small" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = (dim) => {
    confirmAlert({
      title: `حذف مقاس ؟`,
      message: `تنبيه: سيتم حذف هذا المقاس من جميع اللوحات`,
      buttons: [
        {
          label: 'نعم',
          onClick: async () => {
            setSubmitting(true);
            try {
              await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/products/dimension/${dim.id}`,
                { withCredentials: true }
              );
              toast.success(<span className="toast">تم حذف المقاس بنجاح</span>);
              await fetchDimensions();
            } catch {
              toast.error(<span className="toast">لم نتمكن من حذف المقاس. حاول مرة اخرى</span>);
            } finally {
              setSubmitting(false);
            }
          },
        },
        { label: 'لا' },
      ],
    });
  };

  if (loading) return <PanelSkeleton count={2} />;

  return (
    <div className={styles.pageWrapper}>

      {addDim && (
        <form onSubmit={handleSubmit} ref={formRef} className={styles.formPanel}>

          <div className={styles.formHeader}>
            <h4 className={styles.formTitle}>
              {editingDimId ? "تحديث مقاس" : "إضافة مقاس"}
            </h4>
            <button type="button" onClick={handleInactive} disabled={submitting} className={styles.formCloseBtn}>
              <FiX />
            </button>
          </div>

          <div className={styles.formGrid}>

            <div className={styles.field}>
              <input
                type="number" step={0.01} min={1} max={100000}
                name="width" value={dimension.width}
                placeholder=" "
                onChange={handleChange}
                required
                readOnly={!!editingDimId}
              />
              <label>العرض</label>
              <span className={styles.fieldLine} />
            </div>

            <div className={styles.field}>
              <input
                type="number" step={0.01} min={1} max={100000}
                name="height" value={dimension.height}
                placeholder=" "
                onChange={handleChange}
                required
                readOnly={!!editingDimId}
              />
              <label>الارتفاع</label>
              <span className={styles.fieldLine} />
            </div>

            <div className={styles.field}>
              <input
                type="number" step={0.01} min={0} max={100000}
                name="mattePrice" value={dimension.mattePrice}
                placeholder=" "
                onChange={handleChange}
                required
              />
              <label>سعر المط</label>
              <span className={styles.fieldLine} />
            </div>

            <div className={styles.field}>
              <input
                type="number" step={0.01} min={0} max={100000}
                name="glossPrice" value={dimension.glossPrice}
                placeholder=" "
                onChange={handleChange}
                required
              />
              <label>سعر اللامع</label>
              <span className={styles.fieldLine} />
            </div>

          </div>

          {/* Van type toggle */}
          <div className={styles.vanToggleWrap}>
            <span className={styles.vanToggleLabel}>نوع السيارة</span>
            <div className={styles.vanToggle}>
              <button
                type="button"
                className={`${styles.vanBtn} ${dimension.vanType === "small" ? styles.vanBtnActive : ""}`}
                onClick={() => setDimension(prev => ({ ...prev, vanType: "small" }))}
              >
                سيارة صغيرة
              </button>
              <button
                type="button"
                className={`${styles.vanBtn} ${dimension.vanType === "big" ? styles.vanBtnActiveBig : ""}`}
                onClick={() => setDimension(prev => ({ ...prev, vanType: "big" }))}
              >
                سيارة كبيرة
              </button>
            </div>
          </div>

          <button type="submit" disabled={submitting} className={styles.submitBtn}>
            {submitting
              ? <ImSpinner2 className={styles.spinner} />
              : editingDimId
                ? <FiRotateCw className={styles.btnIcon} style={{ color: '#fff' }} />
                : <FiSave className={styles.btnIcon} style={{ color: '#fff' }} />
            }
            <span>{editingDimId ? "تحديث" : "حفظ"}</span>
          </button>

        </form>
      )}

      <div className={styles.tableCard}>

        <div className={styles.cardHeader}>
          <span className={styles.cardTitle}>إدارة المقاسات</span>
          {addDim ? (
            <button onClick={handleInactive} disabled={submitting} className={styles.cancelBtnInline}>
              <FiX className={styles.btnIcon} />
              <span>إلغاء</span>
            </button>
          ) : (
            <button onClick={handleActive} disabled={submitting} className={styles.addBtnInline}>
              <FiPlus className={styles.btnIcon} />
              <span>إضافة مقاس</span>
            </button>
          )}
        </div>

        {dimensions.length === 0 ? (
          <div className={styles.emptyState}>
            <FiMaximize2 className={styles.emptyIcon} />
            <p className={styles.emptyText}>لا توجد مقاسات مضافة</p>
          </div>
        ) : (
          <div className={styles.cardsGrid}>
            {dimensions.map((dim, i) => (
              <div
                key={dim.id}
                className={styles.dimCard}
                style={{ animationDelay: `${Math.min(i * 0.07, 0.56)}s` }}
              >
                <div className={`${styles.cardTopBar} ${dim.vanType === "big" ? styles.cardTopBarBig : ""}`} />

                <div className={styles.dimSizeRow}>
                  <span className={styles.dimNum}>{dim.width}</span>
                  <span className={styles.dimCross}>×</span>
                  <span className={styles.dimNum}>{dim.height}</span>
                </div>

                <div className={styles.pricesRow}>
                  <div className={styles.matteBadge}>
                    <span className={styles.badgeLabel}>مط</span>
                    <span className={styles.badgeValue}>{dim.mattePrice}</span>
                  </div>
                  <div className={styles.glossBadge}>
                    <span className={styles.badgeLabel}>لامع</span>
                    <span className={styles.badgeValue}>{dim.glossPrice}</span>
                  </div>
                </div>

                <div className={`${styles.vanTypeBadge} ${dim.vanType === "big" ? styles.vanTypeBadgeBig : styles.vanTypeBadgeSmall}`}>
                  {dim.vanType === "big" ? "سيارة كبيرة" : "سيارة صغيرة"}
                </div>

                <div className={styles.cardActions}>
                  <button onClick={() => handleEdit(dim)} className={styles.editBtn} disabled={submitting}>
                    تعديل
                  </button>
                  <button onClick={() => !submitting && handleDelete(dim)} className={styles.deleteBtn} disabled={submitting}>
                    <FaRegTrashAlt />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default DimensionsPanel;
